import { inject, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FORTRESS_CONFIG, PermissionCheckService } from '@psu-pennstateit-swe/apis/fortress';
import { Observable, shareReplay } from 'rxjs';

export enum CCAPermissions {
  COL_CREATE = 'college-cost.costOfLiving.create',
  COL_DELETE = 'college-cost.costOfLiving.delete',
  COL_READ = 'college-cost.costOfLiving.read',
  FEE_CREATE = 'college-cost.fee.create',
  FEE_DELETE = 'college-cost.fee.delete',
  FEE_READ = 'college-cost.fee.read',
  TUITION_RATE_CREATE = 'college-cost.tuition.load',
  TUITION_RATE_DELETE = 'college-cost.tuition.delete',
  TUITION_RATE_READ = 'college-cost.tuition.read',
  TUITION_RATE_UPDATE = 'college-cost.tuition.update',
}

@Injectable({
  providedIn: 'root',
})
export class PermissionsFacade {
  // Cost of Living
  readonly canCreateCostOfLivings$: Observable<boolean>;
  readonly canDeleteCostOfLivings$: Observable<boolean>;
  readonly canViewCostOfLivings$: Observable<boolean>;
  // Fees
  readonly canCreateFees$: Observable<boolean>;
  readonly canDeleteFees$: Observable<boolean>;
  readonly canViewFees$: Observable<boolean>;
  // Tuition Rates
  readonly canCreateTuitionRates$: Observable<boolean>;
  readonly canDeleteTuitionRates$: Observable<boolean>;
  readonly canUpdateTuitionRates$: Observable<boolean>;
  readonly canViewTuitionRates$: Observable<boolean>;

  readonly permissionMap: Map<string, Observable<boolean>>;

  private readonly activatedRoles = inject(FORTRESS_CONFIG).activatedRoles;
  private readonly permissionCheckService = inject(PermissionCheckService);

  constructor() {
    // Create
    this.canCreateCostOfLivings$ = this.permissionCheckService
      .hasPermission(CCAPermissions.COL_CREATE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canCreateFees$ = this.permissionCheckService
      .hasPermission(CCAPermissions.FEE_CREATE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canCreateTuitionRates$ = this.permissionCheckService
      .hasPermission(CCAPermissions.TUITION_RATE_CREATE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    // Delete
    this.canDeleteCostOfLivings$ = this.permissionCheckService
      .hasPermission(CCAPermissions.COL_DELETE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canDeleteFees$ = this.permissionCheckService
      .hasPermission(CCAPermissions.FEE_DELETE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canDeleteTuitionRates$ = this.permissionCheckService
      .hasPermission(CCAPermissions.TUITION_RATE_DELETE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    // Read
    this.canViewCostOfLivings$ = this.permissionCheckService
      .hasPermission(CCAPermissions.COL_READ, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canViewFees$ = this.permissionCheckService
      .hasPermission(CCAPermissions.FEE_READ, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    this.canViewTuitionRates$ = this.permissionCheckService
      .hasPermission(CCAPermissions.TUITION_RATE_READ, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));
    // Update
    this.canUpdateTuitionRates$ = this.permissionCheckService
      .hasPermission(CCAPermissions.TUITION_RATE_UPDATE, false, this.activatedRoles)
      .pipe(takeUntilDestroyed(), shareReplay(1));

    this.permissionMap = new Map([
      [CCAPermissions.COL_CREATE, this.canCreateCostOfLivings$],
      [CCAPermissions.COL_DELETE, this.canDeleteCostOfLivings$],
      [CCAPermissions.COL_READ, this.canViewCostOfLivings$],
      [CCAPermissions.FEE_CREATE, this.canCreateFees$],
      [CCAPermissions.FEE_DELETE, this.canDeleteFees$],
      [CCAPermissions.FEE_READ, this.canViewFees$],
      [CCAPermissions.TUITION_RATE_CREATE, this.canCreateTuitionRates$],
      [CCAPermissions.TUITION_RATE_DELETE, this.canDeleteTuitionRates$],
      [CCAPermissions.TUITION_RATE_READ, this.canViewTuitionRates$],
    ]);
  }
}
